import type { ReauthKind } from '@felix/domain';
import { Button, Modal, Textarea } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { purchasingError } from './errors.js';
import classes from './Purchasing.module.css';

export interface PasswordDialogProps {
  opened: boolean;
  title: string;
  body: string;
  /** Gerekçe alanı: `required` iade ve iptalde, `optional` onay yorumunda. */
  reason: 'required' | 'optional' | 'none';
  reasonLabel?: string;
  /**
   * Onay ya da imza adımı mı (gönderim, onay, iade); iptalde değil (parola hiç istenmez). Adımda
   * parola alanı yalnız şirket ayarı açıkken ya da sunucu isterse görünür (ADR 0075).
   */
  password: boolean;
  /**
   * Onayın kayıt türü: malzeme talebi `requisition`, sipariş `purchase_order`. Verilmezse adım
   * her zaman parolalıdır.
   */
  reauthKind?: ReauthKind;
  confirmLabel: string;
  /** Onay verilemez (ör. tedarikçinin e-postası yok; M12c-R28): düğme kapalı, metin yolu söyler. */
  blocked?: boolean;
  /** Hata metninin bağlamı (M12c-R20): sipariş ya da mal kabulü kendi metnine bakar. */
  errorScope?: 'po' | 'grn' | 'bank' | 'ledger';
  onClose: () => void;
  /** Hata kodu döner ya da `null` (başarılı). `password` yalnız girildiyse gelir. */
  onConfirm: (input: { reason: string; password?: string }) => Promise<string | null>;
}

/**
 * İmza, onay, iade ve iptal penceresi (M12b): gerekçe ve açık onay adımı (düğme). Oturum yeterlidir
 * (ADR 0075); şirket ayarı "Onayda parolayı her yerde iste" açıkken parola alanı görünür.
 */
export function PasswordDialog(props: PasswordDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const reauth = useReauth(props.reauthKind);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reset = () => {
    setReason('');
    reauth.reset();
    setError(null);
  };
  const close = () => {
    reset();
    props.onClose();
  };
  const missingReason = props.reason === 'required' && reason.trim() === '';
  const missingPassword = props.password && reauth.missing;
  return (
    <Modal
      opened={props.opened}
      onClose={close}
      title={props.title}
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (props.blocked || missingReason || missingPassword) return;
          setBusy(true);
          const code = await props.onConfirm({
            reason: reason.trim(),
            ...(props.password ? reauth.body() : {}),
          });
          setBusy(false);
          if (code) {
            reauth.noteError({ code });
            setError(code);
          } else reset();
        }}
      >
        <p className={classes.muted}>{props.body}</p>
        {props.reason !== 'none' ? (
          <Textarea
            label={props.reasonLabel ?? t('purchasing.password.reason')}
            required={props.reason === 'required'}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            maxLength={1000}
            autosize
            minRows={2}
            mb="sm"
          />
        ) : null}
        {props.password ? (
          <ReauthField reauth={reauth} label={t('purchasing.password.password')} />
        ) : null}
        {error ? (
          <p className={classes.error} role="alert">
            {purchasingError(t, error, props.errorScope)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={close}>
            {t('purchasing.password.back')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={props.blocked || missingReason || missingPassword}
          >
            {props.confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
